import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import PageBanner from '../components/common/PageBanner';
import { Section, Head, Card, Index, SideImage } from '../components/common/Primitives';
import { membership } from '../data/site';
import banner from '../assets/banners/membership.jpg';

const Membership = () => (
  <div>
    <PageBanner
      image={banner}
      kicker="Membership"
      title="Grown for 500 families"
      lead="Membership is not a transaction. It is a place in a community that values purity, sustainability and a harvest grown deliberately small."
    />

    <Section tone="paper">
      <div className="max-w-3xl">
        <Head
          eyebrow="About membership"
          title="Capped on purpose"
          lead="Unlike mass-market organic suppliers we grow only for our members. No oversubscription, no dilution of quality, no compromises. Once the 500 seats are filled, enrollment closes until one becomes available."
        />
      </div>
    </Section>

    {/* Weekly box */}
    <Section tone="paper2" tight>
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <SideImage
          src={membership.boxImage}
          alt="Weekly harvest boxes packed for members"
        />
        <div>
          <Head eyebrow="What is included" title="The weekly harvest box" />
          <ul className="mt-8 space-y-3.5">
            {membership.box.map((b) => (
              <li key={b} className="flex items-start gap-3">
                <Check className="w-4 h-4 text-forest mt-1 shrink-0" />
                <span className="text-soil/75 text-[15px] leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Section>

    <Section tone="paper">
      <div className="grid md:grid-cols-3 gap-5">
        {membership.included.map((f, i) => (
          <Card key={f.title} className="p-7">
            <Index n={i + 1} />
            <h3 className="mt-4 font-display font-semibold text-[1.15rem] text-ink">{f.title}</h3>
            <p className="mt-2.5 text-soil/70 text-[14.5px] leading-relaxed">{f.body}</p>
          </Card>
        ))}
      </div>
    </Section>

    {/* Joining. There is no price on this page on purpose; see membership.access in site.js. */}
    <Section tone="ink" className="fieldgrid">
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <Head
            tone="dark"
            eyebrow="How to join"
            title="Annual membership"
            lead="Premium, because the experience is premium. Annpriya is not about affordability for everyone; it is about availability for the few who value it."
          />
        </div>
        <div className="rounded-2xl bg-white/[0.05] ring-1 ring-white/10 p-8 md:p-10">
          <p className="font-display font-semibold text-[2.5rem] md:text-[3rem] text-lime leading-none">
            {membership.access}
          </p>
          <p className="mt-3 text-paper/55">{membership.accessNote}</p>
          <dl className="mt-8 space-y-5">
            {membership.terms.map((t) => (
              <div key={t.title}>
                <dt className="label text-lime">{t.title}</dt>
                <dd className="mt-1.5 text-paper/65 text-[14.5px] leading-relaxed">{t.body}</dd>
              </div>
            ))}
          </dl>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 mt-9 rounded-full bg-lime text-ink px-7 py-3.5 font-semibold hover:bg-paper transition-colors"
          >
            Request an invitation <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </Section>

    <Section tone="soil">
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <SideImage
          tone="dark"
          src={membership.handoverImage}
          alt="A member harvest box being handed over at the greenhouse"
        />
        <div>
          <Head
            tone="dark"
            eyebrow="More than food"
            title="A lifestyle shift"
            lead="Becoming a member is about choosing quality over convenience, sustainability over shortcuts, and a harvest grown for you over one grown for a market."
          />
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 mt-9 rounded-full bg-lime text-ink px-7 py-3.5 font-semibold hover:bg-paper transition-colors"
          >
            Start the conversation <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </Section>
  </div>
);

export default Membership;
